import { Clipboard } from '@angular/cdk/clipboard';
import { type DestroyRef, Injectable, inject, type WritableSignal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { isNil } from 'lodash-es';
import { timer } from 'rxjs';

@Injectable({
    providedIn: 'root',
})
export class ClipboardService {
    private readonly clipboard = inject(Clipboard);

    copyToClipboard(text: string): Promise<void> {
        // navigator.clipboard is only available for https and localhost. It is undefined for insecure contexts.
        // We prefer using it if available
        if (!isNil(navigator.clipboard)) {
            return navigator.clipboard.writeText(text);
        }

        // Fallback to Angular CDK which uses the older document.execCommand('copy')
        return this.clipboard.copy(text)
            ? Promise.resolve()
            : Promise.reject(Error('Unable to copy'));
    }

    copyWithStatus(
        text: string,
        copyStatus: WritableSignal<'isIdle' | 'isLoading' | 'isSuccess' | 'isError'>,
        destroyRef: DestroyRef,
    ): Promise<void> {
        copyStatus.set('isLoading');

        const resetStatus = (delay: number) => {
            timer(delay)
                .pipe(takeUntilDestroyed(destroyRef))
                .subscribe(() => {
                    if (['isError', 'isSuccess'].includes(copyStatus())) copyStatus.set('isIdle');
                });
        };

        return this.copyToClipboard(text).then(
            () => {
                copyStatus.set('isSuccess');
                resetStatus(3000);
            },
            err => {
                console.error('Could not copy text: ', err);
                copyStatus.set('isError');
                resetStatus(3000);
            },
        );
    }
}
